import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchCasePlanDetail } from '../../api/casePlanDetailApi';
import { fetchLearnerReentryPlan } from '../../api/learnerReentryPlanApi';
import { REENTRY_PLANS_LIST_PATH } from '../../constants/reentry-planning.constants';
import { loadCasePlanDetailData } from '../../utils/case-plan-detail-loader.util';

export const useCasePlanDetailsHook = (casePlanId) => {
  const navigate = useNavigate();
  const [cardProps, setCardProps] = useState(null);
  const [sections, setSections] = useState([]);
  const [isSectionsLoading, setIsSectionsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [collapsedSections, setCollapsedSections] = useState({});

  const handleBack = useCallback(() => {
    navigate(REENTRY_PLANS_LIST_PATH);
  }, [navigate]);

  const handleToggleCollapse = useCallback((sectionId) => {
    setCollapsedSections((previous) => ({
      ...previous,
      [sectionId]: !previous[sectionId],
    }));
  }, []);

  const handleAddWidget = useCallback((_sectionId) => {
    // Widget picker and persistence will be wired in a follow-up slice.
  }, []);

  const handleAddSection = useCallback(() => {
    // Add section flow will be wired in a follow-up slice.
  }, []);

  const handleEditorsClick = useCallback(() => {
    // Editors modal will be wired in a follow-up slice.
  }, []);

  const handleExportClick = useCallback(() => {
    // Export flow will be wired in a follow-up slice.
  }, []);

  const resetLoadingState = useCallback(() => {
    setIsSectionsLoading(true);
    setError(null);
    setCardProps(null);
    setSections([]);
  }, []);

  const applyLoadedDetail = useCallback((detail) => {
    setCardProps(detail.cardProps);
    setSections(detail.sections);
  }, []);

  const applyLoadFailure = useCallback(() => {
    setCardProps(null);
    setSections([]);
    setError('Failed to load case plan details');
  }, []);

  const finishLoading = useCallback(() => {
    setIsSectionsLoading(false);
  }, []);

  const applyMissingCasePlan = useCallback(() => {
    setError('Case plan not found');
    setIsSectionsLoading(false);
  }, []);

  const loadCasePlanDetail = useCallback(
    async (getIsCancelled, signal) => {
      resetLoadingState();

      try {
        const detail = await loadCasePlanDetailData(casePlanId, {
          fetchCasePlanDetail,
          fetchLearnerReentryPlan,
          signal,
        });

        if (getIsCancelled()) {
          return;
        }

        applyLoadedDetail(detail);
      } catch (error) {
        if (getIsCancelled() || error?.name === 'AbortError') {
          return;
        }

        applyLoadFailure();
      } finally {
        if (!getIsCancelled()) {
          finishLoading();
        }
      }
    },
    [casePlanId, resetLoadingState, applyLoadedDetail, applyLoadFailure, finishLoading],
  );

  useEffect(() => {
    if (!casePlanId) {
      applyMissingCasePlan();
      return;
    }

    const abortController = new AbortController();
    const getIsCancelled = () => abortController.signal.aborted;

    loadCasePlanDetail(getIsCancelled, abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [casePlanId, loadCasePlanDetail, applyMissingCasePlan]);

  return {
    cardProps,
    sections,
    isSectionsLoading,
    error,
    collapsedSections,
    handleBack,
    handleToggleCollapse,
    handleAddWidget,
    handleAddSection,
    handleEditorsClick,
    handleExportClick,
  };
};
